package com.example.positionahead.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.example.positionahead.domain.PlanStrategyBuilder
import com.example.positionahead.model.PlanCompareOption
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.RebalanceTrade
import com.example.positionahead.ui.theme.gainColor
import com.example.positionahead.ui.theme.lossColor
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.util.formatGainLoss
import com.example.positionahead.util.formatPercent
import com.example.positionahead.util.formatPortfolioGainLoss
import com.example.positionahead.util.formatReturnPercentOptional
import com.example.positionahead.util.scaleIncomeToPortfolioSize
import com.example.positionahead.viewmodel.PlanTradePrefill
import com.example.positionahead.viewmodel.PortfolioUiState

@Composable
fun PlanScreen(
    uiState: PortfolioUiState,
    onOpenSettingsGoals: () -> Unit,
    onOpenPlaybook: () -> Unit = {},
    onSavePlanSlot: (columnIndex: Int) -> Unit,
    onRefreshPlan: () -> Unit,
    onSelectPlanSlot: (columnIndex: Int, optionId: String) -> Unit,
    onBuyTrade: (PlanTradePrefill) -> Unit,
    onSellTrade: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val current = uiState.summary
    val options = uiState.planCompareOptions
    val optionB = options.find { it.id == uiState.planSlotBId }
    val optionC = options.find { it.id == uiState.planSlotCId }

    var drillColumn by remember { mutableStateOf<Int?>(null) }
    var drillSymbol by remember { mutableStateOf<String?>(null) }
    var selectedColumn by remember { mutableStateOf(2) }

    val selectedOption = when (selectedColumn) {
        1 -> null
        2 -> optionB
        3 -> optionC
        else -> optionB
    }

    LazyColumn(
        modifier = modifier.fillMaxSize().background(MaterialTheme.colorScheme.background),
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        item {
            PlanCompareUnifiedCard(
                currentName = uiState.activePortfolioName,
                current = current,
                optionB = optionB,
                optionC = optionC,
                compareOptions = options,
                selectedColumn = selectedColumn,
                activeDrillColumn = drillColumn,
                usesLiveQuotes = uiState.usesLiveQuotes,
                onOpenSettingsGoals = onOpenSettingsGoals,
                onRefreshPlan = onRefreshPlan,
                onSelectSlotB = { onSelectPlanSlot(2, it) },
                onSelectSlotC = { onSelectPlanSlot(3, it) },
                onSelectColumn = { selectedColumn = it },
                onOpenHoldings = { col ->
                    drillColumn = col
                    drillSymbol = null
                },
            )
        }

        when (drillColumn) {
            1 -> item {
                PortfolioDrillDown(
                    label = "Current · ${uiState.activePortfolioName}",
                    summary = current,
                    vsCurrent = null,
                    onBack = { drillColumn = null; drillSymbol = null },
                    drillSymbol = drillSymbol,
                    onSymbolClick = { drillSymbol = it.ifBlank { null } },
                )
            }
            2 -> optionB?.let { opt ->
                item {
                    PortfolioDrillDown(
                        label = opt.title,
                        summary = opt.summary,
                        vsCurrent = current,
                        alternativeWhy = opt.whyDifferent,
                        onBack = { drillColumn = null; drillSymbol = null },
                        drillSymbol = drillSymbol,
                        onSymbolClick = { drillSymbol = it.ifBlank { null } },
                    )
                }
            }
            3 -> optionC?.let { opt ->
                item {
                    PortfolioDrillDown(
                        label = opt.title,
                        summary = opt.summary,
                        vsCurrent = current,
                        alternativeWhy = opt.whyDifferent,
                        onBack = { drillColumn = null; drillSymbol = null },
                        drillSymbol = drillSymbol,
                        onSymbolClick = { drillSymbol = it.ifBlank { null } },
                    )
                }
            }
        }

        if (options.isEmpty()) {
            item {
                Text("Import holdings and refresh to load comparison strategies.", style = MaterialTheme.typography.bodyMedium)
            }
        }

        item {
            ActionPlanSection(
                selectedColumn = selectedColumn,
                selectedStrategyTitle = selectedOption?.title ?: "this strategy",
                current = current,
                selectedOption = selectedOption,
                trades = uiState.rebalanceTrades,
                onBuyTrade = onBuyTrade,
                onSellTrade = onSellTrade,
            )
        }

        item {
            Button(
                onClick = { onSavePlanSlot(selectedColumn) },
                modifier = Modifier.fillMaxWidth(),
                enabled = selectedColumn in 2..3 && selectedOption != null,
            ) {
                Text(
                    selectedOption?.let { "Save “${it.title}” as theoretical portfolio" }
                        ?: "Save selected strategy",
                )
            }
        }

        item {
            TextButton(onClick = onOpenPlaybook, modifier = Modifier.fillMaxWidth()) {
                Text("Capital playbook — “what if I invest \$X next?”")
            }
        }
    }
}

private val planDividerColor @Composable get() = MaterialTheme.colorScheme.outline.copy(alpha = 0.55f)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun PlanCompareUnifiedCard(
    currentName: String,
    current: PortfolioSummary,
    optionB: PlanCompareOption?,
    optionC: PlanCompareOption?,
    compareOptions: List<PlanCompareOption>,
    selectedColumn: Int,
    activeDrillColumn: Int?,
    usesLiveQuotes: Boolean,
    onOpenSettingsGoals: () -> Unit,
    onRefreshPlan: () -> Unit,
    onSelectSlotB: (String) -> Unit,
    onSelectSlotC: (String) -> Unit,
    onSelectColumn: (Int) -> Unit,
    onOpenHoldings: (Int) -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
    ) {
        Column(modifier = Modifier.padding(horizontal = 12.dp, vertical = 12.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Compare", fontWeight = FontWeight.Black, style = MaterialTheme.typography.titleMedium)
                Row {
                    TextButton(onClick = onOpenSettingsGoals) { Text("Goals", style = MaterialTheme.typography.labelLarge) }
                    TextButton(onClick = onRefreshPlan) { Text("Refresh", style = MaterialTheme.typography.labelLarge) }
                }
            }
            Text(
                "① Tap a column to select it for trades & save  ·  ② Holdings opens position detail",
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            if (!usesLiveQuotes) {
                Text(
                    "Add FMP API key in Settings for live quotes; G/L still works from your import prices.",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.error.copy(alpha = 0.85f),
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
                verticalAlignment = Alignment.Top,
            ) {
                CompareColumnHeader(
                    modifier = Modifier.weight(1f),
                    columnIndex = 1,
                    heading = "Current",
                    accentLabel = "You",
                    subtitle = currentName,
                    selectedOption = null,
                    compareOptions = emptyList(),
                    isSelected = selectedColumn == 1,
                    isDrillOpen = activeDrillColumn == 1,
                    onSelectOption = {},
                    onSelectColumn = { onSelectColumn(1) },
                    onOpenHoldings = { onOpenHoldings(1) },
                )
                VerticalDivider(
                    modifier = Modifier.height(88.dp).padding(horizontal = 2.dp),
                    thickness = 2.dp,
                    color = planDividerColor,
                )
                CompareColumnHeader(
                    modifier = Modifier.weight(1f),
                    columnIndex = 2,
                    heading = "Alt B",
                    accentLabel = "B",
                    subtitle = optionB?.title ?: "Pick strategy",
                    selectedOption = optionB,
                    compareOptions = compareOptions,
                    isSelected = selectedColumn == 2,
                    isDrillOpen = activeDrillColumn == 2,
                    onSelectOption = onSelectSlotB,
                    onSelectColumn = { onSelectColumn(2) },
                    onOpenHoldings = { if (optionB != null) onOpenHoldings(2) },
                )
                VerticalDivider(
                    modifier = Modifier.height(88.dp).padding(horizontal = 2.dp),
                    thickness = 2.dp,
                    color = planDividerColor,
                )
                CompareColumnHeader(
                    modifier = Modifier.weight(1f),
                    columnIndex = 3,
                    heading = "Alt C",
                    accentLabel = "C",
                    subtitle = optionC?.title ?: "Pick strategy",
                    selectedOption = optionC,
                    compareOptions = compareOptions,
                    isSelected = selectedColumn == 3,
                    isDrillOpen = activeDrillColumn == 3,
                    onSelectOption = onSelectSlotC,
                    onSelectColumn = { onSelectColumn(3) },
                    onOpenHoldings = { if (optionC != null) onOpenHoldings(3) },
                )
            }

            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), thickness = 1.dp)

            Text(
                "Size-normalized rows compare fairly when totals differ",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            CompareMetricRow("Total $", current, optionB?.summary, optionC?.summary, selectedColumn) {
                formatCurrency(it.totalMarketValue)
            }
            CompareMetricRow("G/L", current, optionB?.summary, optionC?.summary, selectedColumn) {
                formatPortfolioGainLoss(it)
            }
            CompareMetricRow("Div yield", current, optionB?.summary, optionC?.summary, selectedColumn) { s ->
                val y = if (s.totalMarketValue > 0) s.estimatedAnnualIncome / s.totalMarketValue * 100.0 else 0.0
                formatPercent(y)
            }
            CompareMetricRow("Div @ your $", current, optionB?.summary, optionC?.summary, selectedColumn) { s ->
                formatCurrency(
                    scaleIncomeToPortfolioSize(
                        s.estimatedAnnualIncome,
                        fromTotal = s.totalMarketValue,
                        toTotal = current.totalMarketValue,
                    ),
                )
            }
            CompareMetricRow("1 mo", current, optionB?.summary, optionC?.summary, selectedColumn) {
                formatReturnPercentOptional(it.return1MonthPercent)
            }
            CompareMetricRow("YTD", current, optionB?.summary, optionC?.summary, selectedColumn) {
                formatReturnPercentOptional(it.returnYtdPercent)
            }
            CompareMetricRow("Stocks", current, optionB?.summary, optionC?.summary, selectedColumn) {
                formatPercent(it.stockAllocationPercent)
            }
            CompareMetricRow("Bonds", current, optionB?.summary, optionC?.summary, selectedColumn) {
                formatPercent(it.bondAllocationPercent)
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun CompareColumnHeader(
    columnIndex: Int,
    heading: String,
    accentLabel: String,
    subtitle: String,
    selectedOption: PlanCompareOption?,
    compareOptions: List<PlanCompareOption>,
    isSelected: Boolean,
    isDrillOpen: Boolean,
    onSelectOption: (String) -> Unit,
    onSelectColumn: () -> Unit,
    onOpenHoldings: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var menuOpen by remember { mutableStateOf(false) }
    val accentColor = when (columnIndex) {
        2 -> MaterialTheme.colorScheme.primary
        3 -> MaterialTheme.colorScheme.tertiary
        else -> MaterialTheme.colorScheme.outline
    }
    val bg = when {
        isSelected -> accentColor.copy(alpha = 0.12f)
        isDrillOpen -> MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.45f)
        else -> MaterialTheme.colorScheme.surface
    }
    val borderColor = if (isSelected) accentColor else MaterialTheme.colorScheme.outline.copy(alpha = 0.25f)

    Column(
        modifier = modifier
            .clip(RoundedCornerShape(10.dp))
            .border(width = if (isSelected) 2.dp else 1.dp, color = borderColor, shape = RoundedCornerShape(10.dp))
            .background(bg)
            .clickable(onClick = onSelectColumn)
            .padding(horizontal = 4.dp, vertical = 6.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(
                accentLabel,
                modifier = Modifier
                    .clip(RoundedCornerShape(4.dp))
                    .background(accentColor.copy(alpha = 0.22f))
                    .padding(horizontal = 5.dp, vertical = 1.dp),
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Bold,
                color = accentColor,
            )
            if (isSelected) {
                Text("✓", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = accentColor)
            }
        }
        Text(
            heading,
            fontWeight = FontWeight.SemiBold,
            style = MaterialTheme.typography.labelMedium,
            textAlign = TextAlign.Center,
        )
        if (columnIndex > 1 && compareOptions.isNotEmpty()) {
            ExposedDropdownMenuBox(
                expanded = menuOpen,
                onExpandedChange = { menuOpen = it },
                modifier = Modifier.fillMaxWidth(),
            ) {
                TextButton(
                    onClick = { menuOpen = true },
                    modifier = Modifier.fillMaxWidth().menuAnchor(),
                    contentPadding = PaddingValues(0.dp),
                ) {
                    Text(
                        selectedOption?.title ?: "Choose…",
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Medium,
                        textAlign = TextAlign.Center,
                    )
                }
                ExposedDropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                    compareOptions.forEach { opt ->
                        DropdownMenuItem(
                            text = { Text(opt.title, style = MaterialTheme.typography.bodySmall) },
                            onClick = {
                                onSelectOption(opt.id)
                                menuOpen = false
                            },
                        )
                    }
                }
            }
        } else {
            Text(
                subtitle,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Medium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
            )
        }
        TextButton(
            onClick = onOpenHoldings,
            contentPadding = PaddingValues(horizontal = 0.dp, vertical = 0.dp),
        ) {
            Text("Holdings", style = MaterialTheme.typography.labelSmall)
        }
    }
}

@Composable
private fun CompareMetricRow(
    label: String,
    current: PortfolioSummary,
    altB: PortfolioSummary?,
    altC: PortfolioSummary?,
    selectedColumn: Int,
    format: (PortfolioSummary) -> String,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 3.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            label,
            modifier = Modifier.weight(0.55f),
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.Medium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        CompareMetricCell(format(current), columnIndex = 1, selectedColumn = selectedColumn, modifier = Modifier.weight(1f))
        CompareMetricCell(altB?.let(format) ?: "—", columnIndex = 2, selectedColumn = selectedColumn, modifier = Modifier.weight(1f))
        CompareMetricCell(altC?.let(format) ?: "—", columnIndex = 3, selectedColumn = selectedColumn, modifier = Modifier.weight(1f))
    }
}

@Composable
private fun CompareMetricCell(
    text: String,
    columnIndex: Int,
    selectedColumn: Int,
    modifier: Modifier = Modifier,
) {
    val highlight = selectedColumn == columnIndex
    Text(
        text,
        modifier = modifier
            .clip(RoundedCornerShape(6.dp))
            .then(
                if (highlight) {
                    Modifier.background(MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.35f))
                } else {
                    Modifier
                },
            )
            .padding(vertical = 2.dp),
        style = MaterialTheme.typography.labelMedium,
        fontWeight = FontWeight.SemiBold,
        maxLines = 2,
        overflow = TextOverflow.Ellipsis,
        textAlign = TextAlign.Center,
    )
}

@Composable
private fun PortfolioDrillDown(
    label: String,
    summary: PortfolioSummary,
    vsCurrent: PortfolioSummary?,
    alternativeWhy: String = "",
    onBack: () -> Unit,
    drillSymbol: String?,
    onSymbolClick: (String) -> Unit,
) {
    val diffs = remember(summary, vsCurrent, alternativeWhy) {
        if (vsCurrent != null) {
            PlanStrategyBuilder.diffVsCurrent(vsCurrent, summary, alternativeWhy)
        } else {
            emptyList()
        }
    }

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(label, fontWeight = FontWeight.Black, style = MaterialTheme.typography.titleMedium)
                OutlinedButton(onClick = onBack) { Text("Close") }
            }
            if (alternativeWhy.isNotBlank()) {
                Text(alternativeWhy, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }

            if (vsCurrent != null && diffs.isNotEmpty()) {
                Text("What's different vs current", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleSmall)
                diffs.take(12).forEach { diff ->
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(8.dp))
                            .background(MaterialTheme.colorScheme.background)
                            .clickable { onSymbolClick(diff.symbol) }
                            .padding(10.dp),
                    ) {
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text(diff.symbol, fontWeight = FontWeight.Bold)
                            Text(
                                (if (diff.deltaDollars >= 0) "+" else "") + formatCurrency(diff.deltaDollars),
                                color = if (diff.deltaDollars >= 0) gainColor else lossColor,
                                fontWeight = FontWeight.SemiBold,
                            )
                        }
                        Text(
                            "${formatCurrency(diff.currentMarketValue)} → ${formatCurrency(diff.alternativeMarketValue)} · " +
                                "${formatPercent(diff.currentWeightPercent)} → ${formatPercent(diff.alternativeWeightPercent)}",
                            style = MaterialTheme.typography.labelSmall,
                        )
                        Text(diff.explanation, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                }
            }

            HorizontalDivider()

            Text("All positions", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleSmall)
            Row(modifier = Modifier.fillMaxWidth()) {
                Text("Symbol", Modifier.weight(1.2f), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                Text("Value", Modifier.weight(1f), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                Text("Wt%", Modifier.weight(0.7f), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                Text("G/L", Modifier.weight(1f), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
            }
            summary.positions.sortedByDescending { it.marketValue }.forEach { snap ->
                val sym = snap.position.symbol.uppercase()
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onSymbolClick(sym) }
                        .padding(vertical = 6.dp),
                ) {
                    Row(modifier = Modifier.fillMaxWidth()) {
                        Text(sym, Modifier.weight(1.2f), style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Medium)
                        Text(formatCurrency(snap.marketValue), Modifier.weight(1f), style = MaterialTheme.typography.bodySmall)
                        Text(formatPercent(snap.portfolioWeightPercent), Modifier.weight(0.7f), style = MaterialTheme.typography.bodySmall)
                        Text(
                            formatGainLoss(snap.unrealizedGainLoss, snap.unrealizedGainLossPercent, snap.hasLivePrice),
                            Modifier.weight(1f),
                            style = MaterialTheme.typography.labelSmall,
                            color = if (snap.unrealizedGainLoss >= 0) gainColor else lossColor,
                        )
                    }
                    Text(
                        "Div ${formatCurrency(snap.estimatedAnnualIncome)}/yr" +
                            (snap.returnYtdPercent?.let { " · YTD ${formatPercent(it)}" } ?: ""),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }

            drillSymbol?.let { symbol ->
                val snap = summary.positions.find { it.position.symbol.equals(symbol, ignoreCase = true) }
                if (snap != null) {
                    HorizontalDivider()
                    Text("$symbol · detail", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleSmall)
                    Text(
                        "Shares & lots: ${snap.lotSnapshots.size} lot(s), total ${snap.lotSnapshots.sumOf { it.lot.shares }.formatShares()} shares",
                        style = MaterialTheme.typography.bodySmall,
                    )
                    snap.lotSnapshots.forEach { lot ->
                        Text(
                            "Lot ${formatCurrency(lot.marketValue)} · ${lot.lot.shares.formatShares()} sh @ ${formatCurrency(lot.lot.costBasisPerShare)} · " +
                                formatGainLoss(lot.unrealizedGainLoss, lot.unrealizedGainLossPercent, snap.hasLivePrice),
                            style = MaterialTheme.typography.labelSmall,
                            modifier = Modifier.padding(vertical = 2.dp),
                        )
                    }
                    OutlinedButton(onClick = { onSymbolClick("") }, modifier = Modifier.padding(top = 4.dp)) {
                        Text("Back to list")
                    }
                }
            }
        }
    }
}

@Composable
private fun ActionPlanSection(
    selectedColumn: Int,
    selectedStrategyTitle: String,
    current: PortfolioSummary,
    selectedOption: PlanCompareOption?,
    trades: List<RebalanceTrade>,
    onBuyTrade: (PlanTradePrefill) -> Unit,
    onSellTrade: (String) -> Unit,
) {
    val accentColor = when (selectedColumn) {
        3 -> MaterialTheme.colorScheme.tertiary
        2 -> MaterialTheme.colorScheme.primary
        else -> MaterialTheme.colorScheme.outline
    }
    val accentLabel = when (selectedColumn) {
        3 -> "C"
        2 -> "B"
        else -> "—"
    }
    val diffs = remember(current, selectedOption) {
        selectedOption?.let { PlanStrategyBuilder.diffVsCurrent(current, it.summary, it.whyDifferent) } ?: emptyList()
    }

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    accentLabel,
                    modifier = Modifier
                        .clip(RoundedCornerShape(6.dp))
                        .background(accentColor.copy(alpha = 0.2f))
                        .padding(horizontal = 8.dp, vertical = 4.dp),
                    fontWeight = FontWeight.Black,
                    color = accentColor,
                )
                Text(
                    "Trades toward $selectedStrategyTitle",
                    fontWeight = FontWeight.Black,
                    style = MaterialTheme.typography.titleSmall,
                    modifier = Modifier.weight(1f),
                )
            }
            when {
                selectedColumn == 1 -> {
                    Text(
                        "Tap Alt B or Alt C above to select a strategy — trades and save apply to that column.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                selectedOption?.id == "strategy_trades" && trades.isNotEmpty() -> {
                    Text("Dollar trades from your goal & concentration limits:", style = MaterialTheme.typography.labelMedium)
                    trades.forEach { trade ->
                        trade.sellSymbol?.let { sell ->
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                Text("Sell ${formatCurrency(trade.sellAmountDollars)} $sell", modifier = Modifier.weight(1f))
                                OutlinedButton(onClick = { onSellTrade(sell) }) { Text("Record") }
                            }
                        }
                        trade.buySymbol?.let { buy ->
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                Text("Buy ${formatCurrency(trade.buyAmountDollars)} $buy", modifier = Modifier.weight(1f))
                                Button(onClick = { onBuyTrade(PlanTradePrefill(buy, trade.buyAmountDollars)) }) { Text("Record") }
                            }
                        }
                    }
                }
                selectedOption != null && diffs.isNotEmpty() -> {
                    Text(
                        "Largest position shifts vs your current holdings (approx. dollars):",
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    diffs.take(6).forEach { diff ->
                        val action = when {
                            diff.deltaDollars > 1 -> "Add ~${formatCurrency(diff.deltaDollars)}"
                            diff.deltaDollars < -1 -> "Trim ~${formatCurrency(-diff.deltaDollars)}"
                            else -> "Hold"
                        }
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(8.dp))
                                .background(MaterialTheme.colorScheme.background)
                                .padding(10.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(diff.symbol, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                                Text(action, style = MaterialTheme.typography.labelSmall)
                            }
                            if (diff.deltaDollars > 1) {
                                Button(onClick = { onBuyTrade(PlanTradePrefill(diff.symbol, diff.deltaDollars)) }) {
                                    Text("Record buy")
                                }
                            } else if (diff.deltaDollars < -1) {
                                OutlinedButton(onClick = { onSellTrade(diff.symbol) }) { Text("Record sell") }
                            }
                        }
                    }
                }
                else -> {
                    Text(
                        "No material trades for this strategy — holdings are already close to your current portfolio.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
    }
}

private fun Double.formatShares(): String {
    val rounded = kotlin.math.round(this * 10000) / 10000.0
    return rounded.toString()
}
